<script lang="ts" module>
  import { type AppError, ErrorType } from '$types/error';
  import platform from 'platform';
  import Button from '$components/button/Button.svelte';
  import { blur } from 'svelte/transition';
  import { tick } from 'svelte';
  import { isValidAddress } from '$lib/utils/isValidAddress';
  import { stateError } from '$states/error.svelte';
  import { goToTransition } from '$utils/goToTransition';
  import { isValidTransactionAsset } from '$lib/utils/isValidTransactionAsset';

  export const requestQRScanner = async (): Promise<string | undefined> => {
    requested = true;

    await tick();

    // Dynamic import of QrScanner from nimiq package
    const QrScanner = (await import('qr-scanner')).default;

    return new Promise((resolve) => {
      resolveOnScanSuccess = resolve;

      const videoElem = document.getElementById('qr-scanner-video');

      if (!videoElem) {
        handleError('QR scanner video element not found');
        return;
      }

      scanner = new QrScanner(videoElem as HTMLVideoElement, onScanSuccess, {
        preferredCamera: 'environment',
        highlightScanRegion: true,
        highlightCodeOutline: true,
      });

      start();
    });
  };

  let resolveOnScanSuccess:
    | ((decodedText: string | undefined | PromiseLike<string | undefined>) => void)
    | null = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let scanner: any;

  let loading = $state(false);

  let requested = $state(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleError = (message: string, error: any = null) => {
    stateError.set({
      type: ErrorType.TOAST,
      rawError: error as AppError,
      message: message,
    });
    loading = false;
  };

  const start = async () => {
    loading = true;

    try {
      // Check for camera availability first
      const QrScanner = (await import('qr-scanner')).default;
      const hasCamera = await QrScanner.hasCamera();

      if (!hasCamera) {
        handleError('No camera found on this device');
        return;
      }

      await scanner.start();
      loading = false;
    } catch (error) {
      // camera navigator.permissions not supported in FF
      const isFirefox =
        platform.name === 'Firefox' ||
        platform.name === 'Firefox for iOs' ||
        platform.name === 'Firefox for Android';

      if (!isFirefox) {
        type PermissionName =
          | 'geolocation'
          | 'notifications'
          | 'persistent-storage'
          | 'push'
          | 'screen-wake-lock'
          | 'xr-spatial-tracking';
        const permissionName = 'camera' as PermissionName;
        navigator.permissions
          .query({ name: permissionName } as PermissionDescriptor)
          .then((res) => {
            if (res.state === 'denied') {
              handleError('No permission to access Camera.', error);
            } else {
              handleError('Failed to start camera', error);
            }
          })
          .catch((err) => handleError('Camera error', err));
      } else {
        handleError('No Camera device found', error);
      }
    }
  };

  const close = async () => {
    requested = false;

    if (scanner) {
      scanner.stop();
      scanner.destroy();
      scanner = null;
    }

    /**
     * Clear promise if not resolved:
     */
    if (resolveOnScanSuccess) {
      resolveOnScanSuccess(undefined);
      resolveOnScanSuccess = null;
    }
  };

  const onScanSuccess = async (result: string | { data: string }): Promise<void> => {
    const decodedText = typeof result === 'string' ? result : result.data;
    let addressToResolve: string | undefined;

    if (isValidAddress(decodedText)) {
      addressToResolve = decodedText;
    }
    // Handle the edge case for MM QR codes
    else if (decodedText.toLowerCase().startsWith('ethereum:')) {
      const ethAddress = decodedText.substring(9);

      const addressPart = ethAddress.split('?')[0];

      if (isValidAddress(addressPart)) {
        addressToResolve = addressPart;
      }
    } else {
      const url = new URL(decodedText);

      /**
       * Handle the external QR code link for EthCC 2025 Ice Cream booth:
       */
      if (url.href === 'https://hovqr.to/5d3790a6') {
        void close();
        void goToTransition('right', '/transfer/0x5Ec8f63b7d4B1A74E416ED3ae7bE1a96957871b8/crc/20');
        return;
      }

      if (url.hostname.endsWith('.metri.xyz') && url.protocol === 'https:') {
        // Handle Cometh's "Add passkey" validation URLs
        // Format: /settings/wallet-owners/validate-passkey?os=...&b=...&p=...&x=...&y=...&id=...&ad=...
        if (url.pathname === '/settings/wallet-owners/validate-passkey') {
          const requiredParams = ['x', 'y', 'id', 'ad'];
          const hasAllRequiredParams = requiredParams.every((param) => url.searchParams.get(param));

          if (hasAllRequiredParams) {
            void close();
            void goToTransition('right', url.pathname + url.search);
            return;
          } else {
            stateError.set({
              type: ErrorType.TOAST,
              rawError: { message: 'Invalid passkey validation QR code' } as AppError,
              message: 'Invalid passkey validation QR code. Missing required parameters.',
            });
            void close();
            return;
          }
        }

        const pathParts = url.pathname.split('/').filter((part) => part.length > 0);

        // Handle transaction URLs
        // Format: /transfer/address/asset/amount
        if (pathParts[0] === 'transfer' && pathParts.length >= 3) {
          const [, address, assetId] = pathParts;

          if (isValidAddress(address)) {
            if (!isValidTransactionAsset(assetId)) {
              stateError.set({
                type: ErrorType.TOAST,
                rawError: { message: 'Invalid asset in QR code' } as AppError,
                message: 'Invalid asset in QR code.',
              });
              void close();
              return;
            }

            void close();
            void goToTransition('right', url.pathname + url.search);
            return;
          }
        }

        // Handle other URLs that contain just an address
        else {
          const pathAddress = url.pathname.split('/').pop();

          if (pathAddress && isValidAddress(pathAddress)) {
            addressToResolve = pathAddress;
          }
        }
      }
    }

    if (addressToResolve) {
      resolve(addressToResolve);
    } else {
      stateError.set({
        type: ErrorType.TOAST,
        rawError: { message: 'Invalid QR code' } as AppError,
        message: 'Invalid QR code. Please scan a valid address or URL.',
      });
    }

    void close();
  };

  const resolve = (value: string) => {
    if (!resolveOnScanSuccess) {
      return;
    }

    resolveOnScanSuccess(value);
    resolveOnScanSuccess = null;
  };
</script>

<script lang="ts">
  import IconXmark from '$lib/icons/IconXmark.svelte';
  import BrandedSpinner from '$components/branded-spinner/BrandedSpinner.svelte';
</script>

{#if requested}
  <div class="fixed left-0 top-0 z-994 h-screen w-screen" in:blur>
    <div class="absolute left-4 top-6 z-10 grid w-full invert pt-safe">
      <Button ariaLabel="Close" variant="secondary" icon onclick={close}>
        <IconXmark />
      </Button>
    </div>
    <div
      class="absolute bottom-6 z-10 grid w-full place-content-center typography-body-l-medium text-foreground-alt-default pb-safe">
      <span class="rounded-xl bg-black px-4 py-3 text-white">Scan Address</span>
    </div>
    <div class="relative h-full w-full place-content-center bg-black">
      {#if loading}
        <BrandedSpinner />
      {/if}
      <div id="qr-scanner" class="overflow-hidden">
        <video id="qr-scanner-video" class="h-full w-full">
          <track kind="captions" src="" />
        </video>
      </div>
    </div>
  </div>
{/if}
